import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';
import AssignmentContainer, {
  type AssignmentListRow,
} from './AssignmentContainer';
import AssigneeInfoLabel from './AssigneeInfoLabel';
import { ICS204_SURFACE } from './assignmentCardStyles';

type AssignmentListTableProps = {
  data: AssignmentListRow[];
  periodNumber: number;
  readOnly: boolean;
  alreadyAddedWorkGroupIds: string[];
};

const columnHelper = createColumnHelper<AssignmentListRow>();

/** Column defs drive the header; row bodies are rendered by AssignmentContainer. */
const columns = [
  columnHelper.display({
    id: 'expand',
    header: () => <span className="tw:sr-only">Expand</span>,
  }),
  columnHelper.accessor('workGroup', {
    id: 'workGroup',
    header: () => <AssigneeInfoLabel />,
  }),
  columnHelper.accessor('proposedStatus', {
    id: 'proposedStatus',
    header: 'Status',
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <span className="tw:sr-only">Actions</span>,
  }),
];

const SUMMARY_COLUMN_KEYS = ['workGroup', 'proposedStatus'] as const;

/**
 * Shadcn Table + TanStack Table (v8) for ICS-204 rows. Expansion and edit state
 * live in each AssignmentContainer (tbody-per-row). Rounded bordered wrapper
 * for a modern card-like table; row separators stay horizontal-only.
 *
 * Overflow-x is clipped (not auto) so the sticky version banner can stick to
 * the planning page scrollport. Nested shadcn table containers are overridden
 * the same way for this widget only.
 */
const AssignmentListTable = ({
  data,
  periodNumber,
  readOnly,
  alreadyAddedWorkGroupIds,
}: AssignmentListTableProps) => {
  const { isVisible: isAiOpen } = useChatSidebar();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.workGroupId,
  });

  return (
    <div
      data-testid="ics204-table-wrapper"
      data-ai-open={isAiOpen ? 'true' : undefined}
      className={cn(
        // Outer list container only: overflow visible so sticky banners work.
        // Nested section tables (e.g. Box 5) keep their own overflow-x-auto.
        'dark tw:w-full tw:min-w-0 tw:overflow-visible tw:rounded-lg tw:border tw:border-[var(--border-primary)] tw:text-foreground',
        'tw:[&>[data-slot=table-container]]:min-w-0 tw:[&>[data-slot=table-container]]:!overflow-visible',
        ICS204_SURFACE
      )}
    >
      <Table
        className={cn(
          ICS204_SURFACE,
          'tw:w-full tw:min-w-0',
          isAiOpen && 'tw:table-fixed'
        )}
      >
        <TableHeader data-testid="ics204-column-header">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="tw:hover:bg-transparent tw:border-b tw:border-[var(--border-primary)]"
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    ICS204_SURFACE,
                    'tw:text-[var(--text-weak)]',
                    isAiOpen && 'tw:whitespace-normal'
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        {data.map((item) => (
          <AssignmentContainer
            key={item.workGroupId}
            item={item}
            columns={[...SUMMARY_COLUMN_KEYS]}
            periodNumber={periodNumber}
            readOnly={readOnly}
            alreadyAddedWorkGroupIds={alreadyAddedWorkGroupIds}
            columnCount={columns.length}
            compact={isAiOpen}
          />
        ))}
        {data.length === 0 && (
          <tbody>
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="tw:h-24 tw:text-center tw:font-normal"
              >
                No results.
              </TableCell>
            </TableRow>
          </tbody>
        )}
      </Table>
    </div>
  );
};

export default AssignmentListTable;
